// src/components/proposal/GapResolutionPanel.tsx
// Redesigned gap resolution panel with semantic gap analysis and improved UX

import React, { useState, useCallback, useMemo } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  Upload,
  FileText,
  Plus,
  X,
  Check,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  ArrowRight,
  Target,
  Users,
  Shield,
  Briefcase,
  Lightbulb,
  FileWarning,
  Zap,
  TrendingUp,
  Clock
} from 'lucide-react';
import {
  ReadinessReport,
  Gap,
  NewDataInput,
  FIELD_ASSOCIATIONS,
  OrchestratedProposalService
} from '../../services/orchestrated-proposal-service';
import { SolicitationUnderstanding } from './SolicitationUnderstanding';

interface GapResolutionPanelProps {
  readinessReport: ReadinessReport;
  onEnrich: (newData: NewDataInput) => Promise<void>;
  onProceed: (acknowledgedGaps: string[]) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface TextInputEntry {
  id: string;
  text: string;
  associatedField: string;
}

interface FileUpload {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  error?: string;
}

// Get icon for requirement type
const getRequirementIcon = (type?: string) => {
  switch (type) {
    case 'staffing':
      return Users;
    case 'certification':
    case 'clearance':
    case 'compliance':
      return Shield;
    case 'experience':
      return Briefcase;
    case 'technical':
      return Target;
    default:
      return AlertCircle;
  }
};

// Get color scheme for match score
const getMatchScoreColor = (score?: number) => {
  if (score === undefined) return 'bg-bw-surface-subtle text-bw-muted';
  if (score >= 70) return 'bg-success-bg text-success';
  if (score >= 30) return 'bg-fit-moderate-bg text-fit-moderate';
  return 'bg-urgent-bg text-urgent';
};

export const GapResolutionPanel: React.FC<GapResolutionPanelProps> = ({
  readinessReport,
  onEnrich,
  onProceed,
  onCancel,
  isLoading = false
}) => {
  const [textInputs, setTextInputs] = useState<TextInputEntry[]>([]);
  const [structuredInputs, setStructuredInputs] = useState<Record<string, string>>({});
  const [fileUploads, setFileUploads] = useState<FileUpload[]>([]);
  const [acknowledgedGaps, setAcknowledgedGaps] = useState<string[]>([]);
  const [expandedSections, setExpandedSections] = useState<string[]>(['additional']);
  const [expandedGaps, setExpandedGaps] = useState<string[]>([]);
  // Human-in-the-loop grounding gates. Default to BidWin's grounded behavior:
  // inferred claims are allowed (with a transparent chain) and gaps are flagged
  // in the draft rather than silently filled.
  const [allowInferred, setAllowInferred] = useState(true);
  const [flagGaps, setFlagGaps] = useState(true);

  const { gaps, overallScore, matches, semanticAnalysis } = readinessReport;

  // Unified, prioritized requirements list (critical → recommended → optional).
  // Critical items expose an "Acknowledge" affordance; the rest are informational.
  const combinedGaps = useMemo(
    () => [
      ...gaps.critical.map(gap => ({ gap, acknowledge: true })),
      ...gaps.recommended.map(gap => ({ gap, acknowledge: false })),
      ...gaps.optional.map(gap => ({ gap, acknowledge: false })),
    ],
    [gaps]
  );

  // Compact eligibility chips shown in the slim readiness header.
  const matchChips = useMemo(
    () => [
      { label: 'NAICS', ok: matches.naicsMatch, soft: false },
      { label: 'Set-Aside', ok: matches.setAsideMatch, soft: false },
      { label: 'Clearance', ok: matches.clearanceMatch, soft: false },
      { label: 'Geographic', ok: matches.geographicMatch, soft: true },
      {
        label: `Experience ${matches.experienceMatch.matched}/${matches.experienceMatch.required}`,
        ok: matches.experienceMatch.matched >= matches.experienceMatch.required,
        soft: true,
      },
    ],
    [matches]
  );

  const metCount = semanticAnalysis?.strongMatches ?? 0;
  const toFixCount = gaps.critical.length + gaps.recommended.length;

  // Calculate which Quick Fill fields to show based on all gaps (not just critical)
  const quickFillFields = useMemo(() => {
    const allGaps = [...gaps.critical, ...gaps.recommended];
    return {
      showAddress: allGaps.some(g => g.field === 'company.address'),
      showPhone: allGaps.some(g => g.field === 'company.phone'),
      showEmail: allGaps.some(g => g.field === 'company.email'),
      showLabor: allGaps.some(g => g.field === 'pricing.labor' || g.field.includes('labor')),
      showClearance: allGaps.some(g => g.field === 'security_clearance' || g.field.includes('clearance')),
      showCapabilities: allGaps.some(g => g.field === 'capabilities' || g.section === 'technical'),
      showPersonnel: allGaps.some(g => g.section === 'personnel' || g.requirementType === 'staffing'),
    };
  }, [gaps]);

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // Toggle gap expansion (for detailed view)
  const toggleGapExpansion = (field: string) => {
    setExpandedGaps(prev =>
      prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  // Add new text input
  const addTextInput = () => {
    setTextInputs(prev => [
      ...prev,
      { id: `text-${Date.now()}`, text: '', associatedField: '' }
    ]);
  };

  // Update text input
  const updateTextInput = (id: string, field: 'text' | 'associatedField', value: string) => {
    setTextInputs(prev =>
      prev.map(input =>
        input.id === id ? { ...input, [field]: value } : input
      )
    );
  };

  // Remove text input
  const removeTextInput = (id: string) => {
    setTextInputs(prev => prev.filter(input => input.id !== id));
  };

  // Handle file selection
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const supportedTypes = OrchestratedProposalService.getSupportedFileTypes();

    for (const file of Array.from(files)) {
      if (!OrchestratedProposalService.validateFileSize(file)) {
        setFileUploads(prev => [
          ...prev,
          { id: `file-${Date.now()}`, file, status: 'error', error: 'File exceeds 2MB limit' }
        ]);
        continue;
      }

      const ext = file.name.toLowerCase().split('.').pop();
      const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv'];

      if (!supportedTypes.includes(file.type) && !allowedExtensions.includes(ext || '')) {
        setFileUploads(prev => [
          ...prev,
          { id: `file-${Date.now()}`, file, status: 'error', error: 'Unsupported file type' }
        ]);
        continue;
      }

      setFileUploads(prev => [
        ...prev,
        { id: `file-${Date.now()}-${file.name}`, file, status: 'pending' }
      ]);
    }

    event.target.value = '';
  }, []);

  // Remove file
  const removeFile = (id: string) => {
    setFileUploads(prev => prev.filter(f => f.id !== id));
  };

  // Toggle gap acknowledgment
  const toggleGapAcknowledgment = (field: string) => {
    setAcknowledgedGaps(prev =>
      prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  // Update structured input
  const updateStructuredInput = (field: string, value: string) => {
    setStructuredInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle re-evaluate
  const handleReEvaluate = async () => {
    const newData: NewDataInput = {
      textInputs: textInputs
        .filter(t => t.text.trim() && t.associatedField)
        .map(t => ({ text: t.text, associatedField: t.associatedField })),
      structuredInputs: Object.fromEntries(
        Object.entries(structuredInputs).filter(([_, v]) => v.trim())
      ),
      documents: await Promise.all(
        fileUploads
          .filter(f => f.status === 'pending')
          .map(async f => ({
            filename: f.file.name,
            content: await OrchestratedProposalService.fileToBase64(f.file),
            mimeType: f.file.type
          }))
      )
    };

    await onEnrich(newData);
  };

  // Handle proceed
  const handleProceed = () => {
    onProceed(acknowledgedGaps);
  };

  // Render enhanced gap item
  const renderGapItem = (gap: Gap, showAcknowledge: boolean = false) => {
    const Icon = getRequirementIcon(gap.requirementType);
    const isExpanded = expandedGaps.includes(gap.field);
    const hasDetails = gap.suggestedAction || gap.relatedEvidence?.length || gap.requirementText;

    return (
      <div
        key={gap.field}
        className="bg-bw-surface rounded-lg border border-bw-border shadow-card overflow-hidden"
      >
        <div className="p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className={`p-1.5 rounded-lg ${
                gap.severity === 'critical' ? 'bg-urgent-bg' :
                gap.severity === 'recommended' ? 'bg-fit-moderate-bg' : 'bg-bw-primary-tint'
              }`}>
                <Icon className={`w-4 h-4 ${
                  gap.severity === 'critical' ? 'text-urgent' :
                  gap.severity === 'recommended' ? 'text-fit-moderate' : 'text-bw-primary'
                }`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-bw-ink leading-snug">
                  {gap.message}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <span className="px-2 py-0.5 bg-bw-surface-subtle rounded text-xs text-bw-muted">
                    {gap.section.replace(/_/g, ' ')}
                  </span>
                  {gap.requirementType && (
                    <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
                      {gap.requirementType}
                    </span>
                  )}
                  {gap.matchScore !== undefined && (
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getMatchScoreColor(gap.matchScore)}`}>
                      {gap.matchScore}% match
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {hasDetails && (
                <button
                  onClick={() => toggleGapExpansion(gap.field)}
                  className="p-1.5 text-bw-muted hover:text-bw-muted hover:bg-bw-surface-subtle rounded transition-colors"
                  title={isExpanded ? 'Hide details' : 'Show details'}
                >
                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
              )}
              {showAcknowledge && (
                <button
                  onClick={() => toggleGapAcknowledgment(gap.field)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    acknowledgedGaps.includes(gap.field)
                      ? 'bg-bw-primary text-white'
                      : 'bg-bw-surface-subtle text-bw-muted hover:bg-bw-border/60'
                  }`}
                >
                  {acknowledgedGaps.includes(gap.field) ? (
                    <span className="flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Acknowledged
                    </span>
                  ) : (
                    'Acknowledge'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Expanded details */}
        {isExpanded && hasDetails && (
          <div className="px-3 pb-3 pt-0 border-t bg-bw-surface-subtle">
            <div className="pt-3 space-y-2">
              {gap.suggestedAction && (
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-4 h-4 text-fit-moderate flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-bw-muted">Suggested Action</p>
                    <p className="text-sm text-bw-ink">{gap.suggestedAction}</p>
                  </div>
                </div>
              )}
              {gap.relatedEvidence && gap.relatedEvidence.length > 0 && (
                <div className="flex items-start gap-2">
                  <Target className="w-4 h-4 text-bw-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-bw-muted">Related Profile Data</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {gap.relatedEvidence.map((evidence, i) => (
                        <span key={i} className="px-2 py-0.5 bg-bw-primary-tint text-bw-primary rounded text-xs">
                          {evidence}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {gap.requirementText && gap.requirementText !== gap.message && (
                <div className="flex items-start gap-2">
                  <FileText className="w-4 h-4 text-bw-muted flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-bw-muted">Full Requirement</p>
                    <p className="text-sm text-bw-ink italic">"{gap.requirementText}"</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Check if we have new data
  const hasNewData = textInputs.some(t => t.text.trim() && t.associatedField) ||
    Object.values(structuredInputs).some(v => v.trim()) ||
    fileUploads.some(f => f.status === 'pending');

  // Check if critical gaps are resolved
  const criticalGapsResolved = gaps.critical.every(g => {
    if (acknowledgedGaps.includes(g.field)) return true;
    if (structuredInputs[g.field]?.trim()) return true;
    if ((g.field === 'pricing.labor' || g.field.includes('labor')) && structuredInputs['pricing.labor']?.trim()) return true;
    if ((g.field === 'security_clearance' || g.field.includes('clearance')) && structuredInputs['security_clearance']?.trim()) return true;
    return false;
  });

  const showQuickFill = Object.values(quickFillFields).some(v => v);

  return (
    <div className="space-y-5">
      {/* ============================================================
          SLIM READINESS HEADER
          Score + eligibility chips + "X met / Y to address" at a glance.
          ============================================================ */}
      <div className="bg-bw-surface rounded-xl border border-bw-border shadow-card p-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <span className={`flex-shrink-0 px-3 py-1.5 rounded-full text-base font-bold font-mono ${
              overallScore >= 70 ? 'bg-fit-strong-bg text-fit-strong' :
              overallScore >= 40 ? 'bg-fit-moderate-bg text-fit-moderate' :
              'bg-urgent-bg text-urgent'
            }`}>
              {overallScore}%
            </span>
            <div className="min-w-0">
              <h3 className="font-semibold text-bw-ink leading-tight">Proposal readiness</h3>
              <p className="text-xs text-bw-muted mt-0.5 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-success" />
                <span><span className="font-medium text-success">{metCount}</span> requirements met</span>
                <span className="text-gray-300">·</span>
                <span><span className="font-medium text-bw-ink">{toFixCount}</span> to address</span>
                {semanticAnalysis && (
                  <>
                    <span className="text-gray-300">·</span>
                    <span>{semanticAnalysis.requirementsCoverage}% coverage</span>
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Eligibility chips */}
          <div className="flex flex-wrap items-center gap-2">
            {matchChips.map(chip => (
              <span
                key={chip.label}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                  chip.ok
                    ? 'bg-success-bg text-success border border-success-border'
                    : chip.soft
                      ? 'bg-fit-moderate-bg text-fit-moderate border border-fit-moderate-border'
                      : 'bg-urgent-bg text-urgent border border-urgent-border'
                }`}
              >
                {chip.ok ? (
                  <Check className="w-3 h-3" />
                ) : chip.soft ? (
                  <AlertCircle className="w-3 h-3" />
                ) : (
                  <X className="w-3 h-3" />
                )}
                {chip.label}
              </span>
            ))}
          </div>
        </div>

        {/* Thin progress bar */}
        <div className="w-full bg-fit-track rounded-full h-2 mt-3 overflow-hidden">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${
              overallScore >= 70 ? 'bg-fit-strong-stroke' :
              overallScore >= 40 ? 'bg-fit-moderate-stroke' :
              'bg-fit-weak-stroke'
            }`}
            style={{ width: `${overallScore}%` }}
          />
        </div>
      </div>

      {/* Contract Update Alert */}
      {semanticAnalysis?.contractUpdatedSince && (
        <div className="bg-fit-moderate-bg border border-fit-moderate-border rounded-xl p-4 flex items-start gap-3">
          <FileWarning className="w-5 h-5 text-fit-moderate flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-fit-moderate text-sm">Contract Updated</h4>
            <p className="text-fit-moderate text-sm mt-0.5">
              This contract was updated on {new Date(semanticAnalysis.contractUpdatedSince).toLocaleDateString()}.
              Re-evaluating may reveal new requirements.
            </p>
          </div>
        </div>
      )}

      {/* ============================================================
          SOLICITATION BRIEF — the centerpiece of this step.
          Renders the authoritative extracted profile; shows a placeholder
          if the profile isn't available yet (extraction failed/unfunded).
          ============================================================ */}
      {readinessReport.collectedData?.solicitationProfile ? (
        <SolicitationUnderstanding profile={readinessReport.collectedData.solicitationProfile} />
      ) : (
        <div className="bg-bw-surface rounded-xl border border-bw-border shadow-card p-5">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-slate-100 text-slate-500">
              <Info className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-bw-ink">Solicitation brief</h3>
              <p className="text-sm text-bw-muted mt-0.5">
                The structured read of this solicitation isn’t available for this analysis yet.
                Re-run the analysis once the solicitation profile can be extracted to see the
                vehicle, evaluation factors, submission volumes, PWS tasks, and required personnel here.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================
          REQUIREMENTS TO ADDRESS — one unified, prioritized checklist
          merging critical / recommended / optional gaps.
          ============================================================ */}
      <div className="bg-bw-surface rounded-xl border border-bw-border shadow-card p-5">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-slate-700" />
            <h3 className="font-semibold text-bw-ink">
              Requirements to address
              {combinedGaps.length > 0 && (
                <span className="ml-2 text-sm font-normal text-bw-muted">({combinedGaps.length})</span>
              )}
            </h3>
          </div>
          {metCount > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success bg-success-bg border border-success-border px-2.5 py-1 rounded-full">
              <Check className="w-3.5 h-3.5" />
              {metCount} requirement{metCount === 1 ? '' : 's'} already met
            </span>
          )}
        </div>

        {gaps.critical.length > 0 && (
          <p className="text-xs text-urgent mb-3 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            Critical items must be resolved or acknowledged before generating.
          </p>
        )}

        {combinedGaps.length === 0 ? (
          <div className="flex items-center gap-3 bg-success-bg border border-success-border rounded-lg p-4">
            <div className="p-2 rounded-lg bg-success-bg text-success">
              <Check className="w-5 h-5" />
            </div>
            <div>
              <p className="font-medium text-success text-sm">No outstanding requirements</p>
              <p className="text-success text-xs mt-0.5">
                Every requirement we extracted is covered by your profile. You’re ready to generate.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {combinedGaps.map(({ gap, acknowledge }) => renderGapItem(gap, acknowledge))}
          </div>
        )}
      </div>

      {/* ============================================================
          PROVIDE MISSING INFO & DOCUMENTS — secondary, collapsible.
          Quick Fill + custom entries + file uploads.
          ============================================================ */}
      <div className="bg-bw-surface rounded-xl border border-bw-border shadow-card overflow-hidden">
        <button
          onClick={() => toggleSection('additional')}
          className="w-full px-4 py-3 flex items-center gap-2 text-left hover:bg-bw-surface-subtle transition-colors"
        >
          {expandedSections.includes('additional') ? (
            <ChevronDown className="w-5 h-5 text-bw-muted" />
          ) : (
            <ChevronRight className="w-5 h-5 text-bw-muted" />
          )}
          <Plus className="w-5 h-5 text-bw-muted" />
          <span className="font-semibold text-bw-ink">Provide missing info &amp; documents</span>
          <span className="text-sm font-normal text-bw-muted ml-1 hidden sm:inline">
            - fill gaps, then re-evaluate
          </span>
        </button>
        {expandedSections.includes('additional') && (
          <div className="px-4 pb-4 space-y-5">
            {/* Quick Fill */}
            {showQuickFill && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-fit-moderate" />
                  <span className="font-medium text-bw-ink text-sm">Quick fill</span>
                  <span className="text-xs text-bw-muted">— rapidly address common gaps</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {quickFillFields.showAddress && (
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1.5">
                        Company Address
                      </label>
                      <input
                        type="text"
                        placeholder="1234 Innovation Dr, Suite 500, Arlington, VA 22201"
                        value={structuredInputs['company.address'] || ''}
                        onChange={e => updateStructuredInput('company.address', e.target.value)}
                        className="w-full px-3 py-2 border border-bw-border rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-transparent text-sm"
                      />
                    </div>
                  )}
                  {quickFillFields.showPhone && (
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1.5">
                        Company Phone
                      </label>
                      <input
                        type="tel"
                        placeholder="(703) 555-1234"
                        value={structuredInputs['company.phone'] || ''}
                        onChange={e => updateStructuredInput('company.phone', e.target.value)}
                        className="w-full px-3 py-2 border border-bw-border rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-transparent text-sm"
                      />
                    </div>
                  )}
                  {quickFillFields.showEmail && (
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1.5">
                        Company Email
                      </label>
                      <input
                        type="email"
                        placeholder="contracts@company.com"
                        value={structuredInputs['company.email'] || ''}
                        onChange={e => updateStructuredInput('company.email', e.target.value)}
                        className="w-full px-3 py-2 border border-bw-border rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-transparent text-sm"
                      />
                    </div>
                  )}
                  {quickFillFields.showClearance && (
                    <div>
                      <label className="block text-sm font-medium text-bw-ink mb-1.5">
                        Security Clearance Level
                      </label>
                      <select
                        value={structuredInputs['security_clearance'] || ''}
                        onChange={e => updateStructuredInput('security_clearance', e.target.value)}
                        className="w-full px-3 py-2 border border-bw-border rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-transparent text-sm"
                      >
                        <option value="">Select clearance level...</option>
                        <option value="Secret">Secret</option>
                        <option value="Top Secret">Top Secret</option>
                        <option value="Top Secret/SCI">Top Secret/SCI</option>
                        <option value="Public Trust">Public Trust</option>
                        <option value="None Required">None Required</option>
                      </select>
                    </div>
                  )}
                </div>

                {quickFillFields.showLabor && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-bw-ink mb-1.5">
                      Labor Rates
                    </label>
                    <textarea
                      placeholder={"Project Manager: $95/hr\nSoftware Engineer: $85/hr\nData Analyst: $75/hr"}
                      value={structuredInputs['pricing.labor'] || ''}
                      onChange={e => updateStructuredInput('pricing.labor', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-bw-border rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-transparent text-sm font-mono"
                    />
                    <p className="text-xs text-bw-muted mt-1">
                      Enter one role per line with hourly rate (e.g., "Software Engineer: $95/hr")
                    </p>
                  </div>
                )}

                {quickFillFields.showCapabilities && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-bw-ink mb-1.5">
                      Technical Capabilities (brief description)
                    </label>
                    <textarea
                      placeholder="Describe your company's relevant technical capabilities, technologies, and expertise..."
                      value={structuredInputs['capabilities.technical'] || ''}
                      onChange={e => updateStructuredInput('capabilities.technical', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-bw-border rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-transparent text-sm"
                    />
                  </div>
                )}

                <div className="border-t mt-5" />
              </div>
            )}

            {/* Custom text entries */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-bw-ink">Add custom information</span>
                <button
                  onClick={addTextInput}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-bw-muted bg-bw-surface-subtle rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Entry
                </button>
              </div>

              {textInputs.map(input => (
                <div key={input.id} className="flex gap-2 items-start mb-3 p-3 bg-bw-surface-subtle rounded-lg">
                  <div className="flex-1 space-y-2">
                    <select
                      value={input.associatedField}
                      onChange={e => updateTextInput(input.id, 'associatedField', e.target.value)}
                      className="w-full px-3 py-2 border border-bw-border rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-transparent text-sm"
                    >
                      <option value="">What is this information for?</option>
                      {Object.entries(
                        FIELD_ASSOCIATIONS.reduce((acc, field) => {
                          if (!acc[field.category]) acc[field.category] = [];
                          acc[field.category].push(field);
                          return acc;
                        }, {} as Record<string, typeof FIELD_ASSOCIATIONS>)
                      ).map(([category, fields]) => (
                        <optgroup key={category} label={category}>
                          {fields.map(field => (
                            <option key={field.value} value={field.value}>
                              {field.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <textarea
                      placeholder="Paste or type information here..."
                      value={input.text}
                      onChange={e => updateTextInput(input.id, 'text', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-bw-border rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-transparent text-sm"
                    />
                  </div>
                  <button
                    onClick={() => removeTextInput(input.id)}
                    className="p-2 text-bw-muted hover:text-urgent transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* File Upload */}
            <div className="border-2 border-dashed border-bw-border rounded-lg p-4 bg-bw-surface-subtle/50">
              <div className="text-center">
                <Upload className="w-8 h-8 mx-auto text-bw-muted mb-2" />
                <p className="text-sm font-medium text-bw-ink mb-1">
                  Upload Supporting Documents
                </p>
                <p className="text-xs text-bw-muted mb-3">
                  PDF, Word, Excel files - Max 2MB each. Documents will be analyzed and extracted.
                </p>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-bw-ink bg-bw-surface border border-bw-border rounded-lg hover:bg-bw-surface-subtle transition-colors cursor-pointer shadow-sm"
                >
                  Choose Files
                </label>
              </div>

              {/* File list */}
              {fileUploads.length > 0 && (
                <div className="mt-4 space-y-2">
                  {fileUploads.map(upload => (
                    <div
                      key={upload.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        upload.status === 'error' ? 'bg-urgent-bg border-urgent-border' : 'bg-bw-surface border-bw-border'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className={`w-5 h-5 ${
                          upload.status === 'error' ? 'text-red-400' : 'text-bw-muted'
                        }`} />
                        <div>
                          <span className="text-sm font-medium text-bw-ink">{upload.file.name}</span>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-bw-muted">
                              {(upload.file.size / 1024).toFixed(1)} KB
                            </span>
                            {upload.status === 'pending' && (
                              <span className="text-xs text-fit-moderate flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Ready to process
                              </span>
                            )}
                            {upload.status === 'error' && (
                              <span className="text-xs text-urgent">{upload.error}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFile(upload.id)}
                        className="p-1.5 text-bw-muted hover:text-urgent hover:bg-urgent-bg rounded transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Human-in-the-loop grounding gates — control what the AI may assert */}
      <div className="bg-bw-surface rounded-xl border border-bw-border p-4 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-2 text-bw-ink flex-shrink-0">
          <Shield className="w-4 h-4 text-bw-primary" />
          <span className="text-sm font-semibold">Grounding gate</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          {([
            { on: allowInferred, set: setAllowInferred, label: 'Allow inferred claims', hint: 'with a transparent chain' },
            { on: flagGaps, set: setFlagGaps, label: 'Flag gaps in draft', hint: 'never silently filled' },
          ] as const).map((t) => (
            <button
              key={t.label}
              type="button"
              role="switch"
              aria-checked={t.on}
              onClick={() => t.set(!t.on)}
              className="flex items-center gap-2 group"
            >
              <span className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${t.on ? 'bg-bw-primary' : 'bg-bw-border'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-bw-surface transition-all ${t.on ? 'left-[18px]' : 'left-0.5'}`} />
              </span>
              <span className="text-left">
                <span className="block text-[12.5px] font-medium text-bw-ink leading-tight">{t.label}</span>
                <span className="block text-[11px] text-bw-muted leading-tight">{t.hint}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-bw-border bg-bw-surface -mx-6 px-6 -mb-6 pb-6 sticky bottom-0">
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="px-4 py-2 text-bw-muted hover:text-bw-ink hover:bg-bw-surface-subtle rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
        >
          Cancel
        </button>

        <div className="flex items-center gap-3">
          {hasNewData && (
            <button
              onClick={handleReEvaluate}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-4 py-2 text-bw-ink bg-bw-surface border border-bw-border rounded-lg hover:bg-bw-surface-subtle transition-colors text-sm font-medium disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Re-evaluate ({fileUploads.filter(f => f.status === 'pending').length > 0
                    ? `${fileUploads.filter(f => f.status === 'pending').length} files`
                    : 'new data'})
                </>
              )}
            </button>
          )}

          {/* Cost-aware CTA */}
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={handleProceed}
              disabled={isLoading || (!criticalGapsResolved && gaps.critical.length > 0)}
              className="inline-flex items-center gap-2 px-6 py-2 bg-bw-primary text-white rounded-lg hover:brightness-110 transition-all text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {gaps.critical.length > 0 && !criticalGapsResolved ? (
                <>
                  <AlertTriangle className="w-4 h-4" />
                  Address Critical Gaps First
                </>
              ) : (
                <>
                  Generate Proposal
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
            {!(gaps.critical.length > 0 && !criticalGapsResolved) && (
              <span className="text-[11px] text-bw-muted font-mono">
                {flagGaps ? `${toFixCount} gap${toFixCount === 1 ? '' : 's'} flagged` : 'gaps suppressed'} · inferred {allowInferred ? 'allowed' : 'off'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GapResolutionPanel;
